import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  Grid,
  Divider,
  Chip,
  Tooltip,
  Link,
  SvgIcon,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { useNavigate } from "react-router-dom";
import ImageCropperModal from "../../../../widgets/ImageCropperModal";
import {
  API_ENDPOINT,
  PROFILE_BANNER,
  PROFILE_IMAGE,
} from "../../../../constants/constants";
import { toast } from "react-toastify";
import { getToken } from "../../../../utility/utility";
import QRCode from "react-qr-code";
import useApiEp from "../../../../hooks/useApiEp";
import { UserContext } from "../../../context/UserContext";
import BackBtn from "../../../../widgets/BackBtn";

// Utility function to convert labels to camelCase keys matching the API response
function toCamelCase(str) {
  if (!str) return "";
  return str
    .replace(/[^a-zA-Z0-9\s_-]/g, "") // Remove special characters except spaces, underscores, and hyphens
    .toLowerCase()
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (match, index) =>
      index === 0 ? match.toLowerCase() : match.toUpperCase()
    )
    .replace(/\s+|[-_]+/g, ""); // Remove spaces, underscores, and hyphens
}

// Styled Components
const BannerContainer = styled(Box)(({ theme, banner }) => ({
  position: "relative",
  height: 200,
  backgroundColor: theme.palette.grey[300],
  backgroundImage: banner ? `url(${banner})` : "none",
  backgroundSize: "cover",
  backgroundPosition: "center",
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
}));

const BannerUploadButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
}));

const ProfileImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  marginTop: -50,
  marginLeft: theme.spacing(2),
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  border: `4px solid ${theme.palette.background.paper}`,
}));

const ProfileImageUploadButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  right: 0,
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
}));

const XIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z" />
  </SvgIcon>
);

const VisitorProfileView = () => {
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/user/get-profile-view`
  );
  const { reloadUser } = useContext(UserContext);

  const eD = useMemo(
    () => ({
      productCategories: data?.productCategories,
      type: data?.type, // e.g., "single", "double", "triple"
      defProfileBanner: data?.defProfileBanner,
    }),
    [data]
  );

  const user = useMemo(() => data?.user || {}, [data]);
  const formSchema = useMemo(() => data?.form || [], [data]);

  const [uploadType, setUploadType] = useState();
  const [cropperOpen, setCropperOpen] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);

  if (isLoading) return <p>Loading profile...</p>;
  if (isError) return <p>Error: {error?.message || "Something went wrong"}</p>;

  const handleUploadSuccess = (response) => {
    toast.success(response.message);
    reloadUser();
    refetch();
  };

  const socialLinks = [
    { key: "in", icon: <InstagramIcon />, label: "Instagram", url: user.in },
    { key: "x", icon: <XIcon />, label: "X (Twitter)", url: user.x },
    { key: "fb", icon: <FacebookIcon />, label: "Facebook", url: user.fb },
    { key: "ln", icon: <LinkedInIcon />, label: "LinkedIn", url: user.ln },
  ];

  return (
    <Box>
      <BackBtn />
      <ImageCropperModal
        open={cropperOpen}
        onClose={() => setCropperOpen(false)}
        aspectRatio={aspectRatio}
        maxSizeKB={1024}
        uploadUrl={`${API_ENDPOINT}/user/upload-profile-image?uploadType=${uploadType}`}
        headers={{ Authorization: `Bearer ${getToken()}` }}
        onSuccess={handleUploadSuccess}
        onError={(error) => console.error("Upload error:", error)}
      />

      <Box sx={{ maxWidth: 800, mx: "auto", my: 2 }}>
        <Card elevation={3}>
          {/* Banner */}
          <BannerContainer banner={user?.profileBanner || eD?.defProfileBanner}>
            <Tooltip title="Change Banner">
              <BannerUploadButton
                onClick={() => {
                  setAspectRatio(3);
                  setUploadType(PROFILE_BANNER);
                  setCropperOpen(true);
                }}
              >
                <PhotoCameraIcon />
              </BannerUploadButton>
            </Tooltip>
          </BannerContainer>

          {/* Profile Image */}
          <ProfileImageContainer>
            <ProfileAvatar src={user.profileImage} alt={user.firstName}>
              {user.firstName?.charAt(0)}
            </ProfileAvatar>
            <Tooltip title="Change Profile Image">
              <ProfileImageUploadButton
                onClick={() => {
                  setAspectRatio(1);
                  setUploadType(PROFILE_IMAGE);
                  setCropperOpen(true);
                }}
              >
                <PhotoCameraIcon fontSize="small" />
              </ProfileImageUploadButton>
            </Tooltip>
          </ProfileImageContainer>

          {/* Core Profile Header Details */}
          <CardContent>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Box>
                <Typography variant="h5" component="div">
                  {user.title} {user.firstName} {user.lastName}
                </Typography>
                {user.designation && (
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {user.designation}
                  </Typography>
                )}
                {user.barcode_id && (
                  <Chip
                    label={user.barcode_id}
                    color="primary"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            </Box>

            <Grid container spacing={2}>
              {/* Dynamically Generate Fields based on Form Schema */}
              {formSchema.map((field) => {
                const label = field.properties?.label || "";
                const key = toCamelCase(label);
                const value = user[key];

                // Parse grid safely, default to 12 on small screens, use provided size for medium
                const mdGrid = parseInt(field.properties?.grid, 10) || 12;

                switch (field.type) {
                  case "Divider":
                    return (
                      <Grid item xs={12} key={field.id}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>
                    );

                  case "PlainText":
                    return (
                      <Grid item xs={12} key={field.id}>
                        <Typography
                          variant={field.properties?.variant || "h6"}
                          color="primary"
                          sx={{ mt: 1, fontWeight: "bold" }}
                        >
                          {label}
                        </Typography>
                      </Grid>
                    );

                  case "ParagraphDescription":
                    return (
                      <Grid item xs={12} key={field.id}>
                        {/* <Box
                          dangerouslySetInnerHTML={{
                            __html: field.properties?.text,
                          }}
                          sx={{
                            "& p, & li": {
                              margin: 0,
                              color: "text.secondary",
                              fontSize: "0.9rem",
                            },
                            mt: 1,
                          }}
                        /> */}
                      </Grid>
                    );

                  case "CountryStateCity":
                    return (
                      <Grid item xs={12} md={mdGrid} key={field.id}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography variant="body1">
                          {[
                            user?.city?.name,
                            user?.state?.name,
                            user?.country?.name,
                          ]
                            .filter(Boolean)
                            .join(", ") || "-"}
                        </Typography>
                      </Grid>
                    );

                  case "ProductCategories":
                    return (
                      <Grid item xs={12} key={field.id}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {label}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {Array.isArray(value) && value.length > 0 ? (
                            value.map((cat, i) => {
                              let catLabel = cat.MainCategory || "";

                              if (
                                (eD.type === "double" ||
                                  eD.type === "triple") &&
                                cat.Category
                              ) {
                                catLabel += ` > ${cat.Category}`;
                              }

                              if (eD.type === "triple" && cat.SubCategory) {
                                catLabel += ` > ${cat.SubCategory}`;
                              }

                              return (
                                <Chip
                                  key={i}
                                  label={catLabel}
                                  variant="outlined"
                                  sx={{
                                    maxWidth: "100%",
                                    "& .MuiChip-label": {
                                      whiteSpace: "normal",
                                      lineHeight: 1.2,
                                      py: 0.5,
                                    },
                                  }}
                                />
                              );
                            })
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    );

                  case "Checkbox":
                    return (
                      <Grid item xs={12} sm={6} md={mdGrid} key={field.id}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography variant="body1">
                          {value === true ? "Yes" : "No"}
                        </Typography>
                      </Grid>
                    );

                  default:
                    // Standard Fields: TextField, Select, RadioGroup, etc.
                    return (
                      <Grid item xs={12} sm={6} md={mdGrid} key={field.id}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography variant="body1">
                          {value !== undefined && value !== null && value !== ""
                            ? String(value)
                            : "-"}
                        </Typography>
                      </Grid>
                    );
                }
              })}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Social Media Links */}
              {socialLinks.some((link) => link.url) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Social Media
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
                  >
                    {socialLinks.map(({ key, icon, label, url }) =>
                      url ? (
                        <Tooltip key={key} title={label}>
                          <IconButton
                            component={Link}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            color="primary"
                            size="small"
                          >
                            {icon}
                          </IconButton>
                        </Tooltip>
                      ) : null
                    )}
                  </Box>
                </Grid>
              )}

              {/* QR Code */}
              {user?.barcode_id && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    QR Code
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <QRCode value={user.barcode_id} size={150} />
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default VisitorProfileView;
